/**
 * Permission catalog (PLAN.md §4.1). The catalog is code; the template values per role are seeded into
 * `role_permission` per organization, and `user_permission_override` may deviate per user.
 * The client uses this only to render; the server decides (§4.4).
 */
export const RoleKey = {
    admin: 'admin',
    salesLead: 'sales_lead',
    advisor: 'advisor',
    setter: 'setter',
    backOffice: 'back_office',
    financier: 'financier',
    referrer: 'referrer',
    developerReadonly: 'developer_readonly',
};
/** German labels of the system roles (Admin 01, Einstellungen 27). */
export const roleLabels = {
    admin: 'Admin',
    sales_lead: 'Vertriebsleitung',
    advisor: 'Berater',
    setter: 'Setter',
    back_office: 'Backoffice',
    financier: 'Finanzierer',
    referrer: 'Tippgeber',
    developer_readonly: 'Bauträger · Lesezugriff',
};
export const yesNo = ['yes', 'no'];
export const contactsViewScopes = [
    'none',
    'own_tips',
    'assigned_cases',
    'pool_dialer',
    'pool_cases',
    'own_pool',
    'span',
    'all',
];
export const dealsViewScopes = ['none', 'own_tips', 'assigned', 'own', 'span', 'all'];
export const changeStageScopes = ['none', 'own', 'yes'];
export const commissionViewScopes = ['none', 'own', 'span', 'all'];
export const financingCreateScopes = ['none', 'if_assigned', 'yes'];
export const assignFinancierScopes = ['none', 'propose', 'yes'];
export const propertiesViewScopes = ['none', 'on_sale', 'all_on_sale', 'all'];
export const reserveScopes = ['none', 'request', 'yes'];
export const controllingScopes = ['none', 'own', 'tickets', 'span', 'organization'];
export const settingsScopes = ['none', 'team', 'yes'];
export const pkvHealthViewScopes = ['none', 'summary', 'full'];
export const pkvRiskInquiryViewScopes = ['none', 'own_cases', 'span', 'all'];
export const pkvCommissionViewScopes = ['none', 'own', 'all'];
export const noneYes = ['none', 'yes'];
function define(definition) {
    return definition;
}
export const permissionCatalog = {
    'contacts.view': define({
        key: 'contacts.view',
        label: 'Kontakte sehen',
        values: contactsViewScopes,
        sensitive: false,
        template: {
            admin: 'all',
            sales_lead: 'span',
            advisor: 'own_pool',
            setter: 'pool_cases',
            back_office: 'all',
            financier: 'assigned_cases',
            referrer: 'own_tips',
        },
    }),
    'contacts.create': define({
        key: 'contacts.create',
        label: 'Kontakte anlegen',
        values: yesNo,
        sensitive: false,
        template: {
            admin: 'yes',
            sales_lead: 'yes',
            advisor: 'yes',
            setter: 'yes',
            back_office: 'yes',
            financier: 'no',
            referrer: 'yes',
        },
    }),
    'contacts.export': define({
        key: 'contacts.export',
        label: 'Kontakte exportieren',
        values: yesNo,
        sensitive: true,
        template: {
            admin: 'yes',
            sales_lead: 'yes',
            advisor: 'no',
            setter: 'no',
            back_office: 'no',
            financier: 'no',
            referrer: 'no',
        },
    }),
    'contacts.lift_dnc_merge': define({
        key: 'contacts.lift_dnc_merge',
        label: 'DNC aufheben, Kontakte zusammenführen',
        values: yesNo,
        sensitive: true,
        template: {
            admin: 'yes',
            sales_lead: 'no',
            advisor: 'no',
            setter: 'no',
            back_office: 'no',
            financier: 'no',
            referrer: 'no',
        },
    }),
    'deals.view': define({
        key: 'deals.view',
        label: 'Deals sehen',
        values: dealsViewScopes,
        sensitive: false,
        template: {
            admin: 'all',
            sales_lead: 'span',
            advisor: 'own',
            setter: 'none',
            back_office: 'all',
            financier: 'assigned',
            referrer: 'own_tips',
        },
    }),
    'deals.change_stage': define({
        key: 'deals.change_stage',
        label: 'Stage wechseln',
        values: changeStageScopes,
        sensitive: false,
        template: {
            admin: 'yes',
            sales_lead: 'yes',
            advisor: 'own',
            setter: 'none',
            back_office: 'yes',
            financier: 'none',
            referrer: 'none',
        },
    }),
    'deals.lose_after_notarized_cancel': define({
        key: 'deals.lose_after_notarized_cancel',
        label: 'Verloren ab Beurkundet, Storno',
        values: yesNo,
        sensitive: true,
        template: {
            admin: 'yes',
            sales_lead: 'no',
            advisor: 'no',
            setter: 'no',
            back_office: 'no',
            financier: 'no',
            referrer: 'no',
        },
    }),
    'deals.view_commission': define({
        key: 'deals.view_commission',
        label: 'Provision sehen',
        values: commissionViewScopes,
        sensitive: true,
        template: {
            admin: 'all',
            sales_lead: 'span',
            advisor: 'own',
            setter: 'own',
            back_office: 'none',
            financier: 'own',
            referrer: 'own',
        },
    }),
    'financing.create': define({
        key: 'financing.create',
        label: 'Finanzierungen anlegen',
        values: financingCreateScopes,
        sensitive: false,
        template: {
            admin: 'yes',
            sales_lead: 'none',
            advisor: 'yes',
            setter: 'none',
            back_office: 'none',
            financier: 'if_assigned',
            referrer: 'none',
        },
    }),
    'financing.assign_financier': define({
        key: 'financing.assign_financier',
        label: 'Finanzierer zuweisen',
        values: assignFinancierScopes,
        sensitive: false,
        template: {
            admin: 'yes',
            sales_lead: 'yes',
            advisor: 'propose',
            setter: 'none',
            back_office: 'none',
            financier: 'none',
            referrer: 'none',
        },
    }),
    'properties.view': define({
        key: 'properties.view',
        label: 'Objekte sehen',
        values: propertiesViewScopes,
        sensitive: false,
        template: {
            admin: 'all',
            sales_lead: 'all_on_sale',
            advisor: 'on_sale',
            setter: 'none',
            back_office: 'all',
            financier: 'none',
            referrer: 'on_sale',
        },
    }),
    'properties.acquisition': define({
        key: 'properties.acquisition',
        label: 'Ankauf (In Ankauf sehen, anlegen)',
        values: yesNo,
        sensitive: true,
        template: {
            admin: 'yes',
            sales_lead: 'no',
            advisor: 'no',
            setter: 'no',
            back_office: 'yes',
            financier: 'no',
            referrer: 'no',
        },
    }),
    'properties.release': define({
        key: 'properties.release',
        label: 'Objekte freigeben, Provisionssatz setzen',
        values: yesNo,
        sensitive: true,
        template: {
            admin: 'yes',
            sales_lead: 'no',
            advisor: 'no',
            setter: 'no',
            back_office: 'no',
            financier: 'no',
            referrer: 'no',
        },
    }),
    'reservations.reserve': define({
        key: 'reservations.reserve',
        label: 'Reservieren',
        values: reserveScopes,
        sensitive: false,
        template: {
            admin: 'yes',
            sales_lead: 'yes',
            advisor: 'yes',
            setter: 'none',
            back_office: 'yes',
            financier: 'none',
            referrer: 'request',
        },
    }),
    'reservations.approve': define({
        key: 'reservations.approve',
        label: 'Reservierungen freigeben',
        values: yesNo,
        sensitive: true,
        template: {
            admin: 'yes',
            sales_lead: 'no',
            advisor: 'no',
            setter: 'no',
            back_office: 'no',
            financier: 'no',
            referrer: 'no',
        },
    }),
    'reservations.release_foreign': define({
        key: 'reservations.release_foreign',
        label: 'Fremde Reservierungen aufheben',
        values: yesNo,
        sensitive: true,
        template: {
            admin: 'yes',
            sales_lead: 'no',
            advisor: 'no',
            setter: 'no',
            back_office: 'no',
            financier: 'no',
            referrer: 'no',
        },
    }),
    'tools.dialer': define({
        key: 'tools.dialer',
        label: 'Dialer',
        values: yesNo,
        sensitive: false,
        template: {
            admin: 'yes',
            sales_lead: 'yes',
            advisor: 'yes',
            setter: 'yes',
            back_office: 'no',
            financier: 'no',
            referrer: 'no',
        },
    }),
    'tools.tickets': define({
        key: 'tools.tickets',
        label: 'Tickets',
        values: yesNo,
        sensitive: false,
        template: {
            admin: 'yes',
            sales_lead: 'no',
            advisor: 'no',
            setter: 'no',
            back_office: 'yes',
            financier: 'no',
            referrer: 'no',
        },
    }),
    'tools.deals': define({
        key: 'tools.deals',
        label: 'Deals',
        values: yesNo,
        sensitive: false,
        template: {
            admin: 'yes',
            sales_lead: 'yes',
            advisor: 'yes',
            setter: 'no',
            back_office: 'yes',
            financier: 'no',
            referrer: 'yes',
        },
    }),
    'tools.financing': define({
        key: 'tools.financing',
        label: 'Finanzierungen',
        values: yesNo,
        sensitive: false,
        template: {
            admin: 'yes',
            sales_lead: 'yes',
            advisor: 'yes',
            setter: 'no',
            back_office: 'yes',
            financier: 'yes',
            referrer: 'no',
        },
    }),
    'tools.pkv': define({
        key: 'tools.pkv',
        label: 'PKV',
        values: yesNo,
        sensitive: false,
        template: {
            admin: 'yes',
            sales_lead: 'yes',
            advisor: 'yes',
            setter: 'yes',
            back_office: 'yes',
            financier: 'no',
            referrer: 'no',
        },
    }),
    'controlling.figures': define({
        key: 'controlling.figures',
        label: 'Controlling-Zahlen',
        values: controllingScopes,
        sensitive: false,
        template: {
            admin: 'organization',
            sales_lead: 'span',
            advisor: 'own',
            setter: 'own',
            back_office: 'tickets',
            financier: 'own',
            referrer: 'own',
        },
    }),
    'settings.organization': define({
        key: 'settings.organization',
        label: 'Organisationseinstellungen',
        values: settingsScopes,
        sensitive: true,
        template: {
            admin: 'yes',
            sales_lead: 'team',
            advisor: 'none',
            setter: 'none',
            back_office: 'none',
            financier: 'none',
            referrer: 'none',
        },
    }),
    'pkv.deals.view': define({
        key: 'pkv.deals.view',
        label: 'PKV-Deals sehen',
        values: dealsViewScopes,
        sensitive: false,
        template: {
            admin: 'all',
            sales_lead: 'span',
            advisor: 'own',
            setter: 'assigned',
            back_office: 'all',
            financier: 'none',
            referrer: 'own_tips',
        },
    }),
    'pkv.deals.change_stage': define({
        key: 'pkv.deals.change_stage',
        label: 'PKV-Phase ändern',
        values: changeStageScopes,
        sensitive: false,
        template: {
            admin: 'yes',
            sales_lead: 'yes',
            advisor: 'own',
            setter: 'none',
            back_office: 'yes',
            financier: 'none',
            referrer: 'none',
        },
    }),
    'pkv.health.view': define({
        key: 'pkv.health.view',
        label: 'Gesundheitsangaben sehen',
        values: pkvHealthViewScopes,
        sensitive: true,
        template: {
            admin: 'full',
            sales_lead: 'full',
            advisor: 'full',
            setter: 'none',
            back_office: 'full',
            financier: 'none',
            referrer: 'none',
        },
    }),
    'pkv.health.edit': define({
        key: 'pkv.health.edit',
        label: 'Gesundheitsangaben bearbeiten',
        values: noneYes,
        sensitive: true,
        template: {
            admin: 'yes',
            sales_lead: 'yes',
            advisor: 'yes',
            setter: 'none',
            back_office: 'yes',
            financier: 'none',
            referrer: 'none',
        },
    }),
    'pkv.risk_inquiry.view': define({
        key: 'pkv.risk_inquiry.view',
        label: 'Risikovoranfragen sehen',
        values: pkvRiskInquiryViewScopes,
        sensitive: false,
        template: {
            admin: 'all',
            sales_lead: 'span',
            advisor: 'own_cases',
            setter: 'none',
            back_office: 'all',
            financier: 'none',
            referrer: 'none',
        },
    }),
    'pkv.risk_inquiry.process': define({
        key: 'pkv.risk_inquiry.process',
        label: 'Risikovoranfragen bearbeiten',
        values: noneYes,
        sensitive: false,
        template: {
            admin: 'yes',
            sales_lead: 'yes',
            advisor: 'yes',
            setter: 'none',
            back_office: 'yes',
            financier: 'none',
            referrer: 'none',
        },
    }),
    'pkv.risk_inquiry.release': define({
        key: 'pkv.risk_inquiry.release',
        label: 'Ergebnisse freigeben',
        values: noneYes,
        sensitive: false,
        template: {
            admin: 'yes',
            sales_lead: 'yes',
            advisor: 'yes',
            setter: 'none',
            back_office: 'yes',
            financier: 'none',
            referrer: 'none',
        },
    }),
    'pkv.calculator': define({
        key: 'pkv.calculator',
        label: 'PKV-Rechner',
        values: noneYes,
        sensitive: false,
        template: {
            admin: 'yes',
            sales_lead: 'yes',
            advisor: 'yes',
            setter: 'none',
            back_office: 'yes',
            financier: 'none',
            referrer: 'none',
        },
    }),
    'pkv.application.generate': define({
        key: 'pkv.application.generate',
        label: 'Antrag erzeugen',
        values: noneYes,
        sensitive: false,
        template: {
            admin: 'yes',
            sales_lead: 'yes',
            advisor: 'yes',
            setter: 'none',
            back_office: 'yes',
            financier: 'none',
            referrer: 'none',
        },
    }),
    'pkv.commission.view': define({
        key: 'pkv.commission.view',
        label: 'PKV-Provisionen sehen',
        values: pkvCommissionViewScopes,
        sensitive: true,
        template: {
            admin: 'all',
            sales_lead: 'all',
            advisor: 'own',
            setter: 'none',
            back_office: 'all',
            financier: 'none',
            referrer: 'none',
        },
    }),
    'pkv.commission.book': define({
        key: 'pkv.commission.book',
        label: 'PKV-Provisionen buchen',
        values: noneYes,
        sensitive: true,
        template: {
            admin: 'yes',
            sales_lead: 'none',
            advisor: 'none',
            setter: 'none',
            back_office: 'yes',
            financier: 'none',
            referrer: 'none',
        },
    }),
};
export const permissionKeys = Object.keys(permissionCatalog);
export function isPermissionKey(value) {
    return Object.hasOwn(permissionCatalog, value);
}
export function isPermissionValue(key, value) {
    return permissionCatalog[key].values.includes(value);
}
/** Template of a system role; custom roles are created from the advisor template (Admin 01). */
export function roleTemplate(roleKey) {
    const systemKeys = Object.values(RoleKey);
    const base = systemKeys.includes(roleKey) ? roleKey : 'advisor';
    const result = {};
    for (const key of permissionKeys) {
        const definition = permissionCatalog[key];
        if (base === 'developer_readonly') {
            result[key] = definition.values.includes('none') ? 'none' : 'no';
        }
        else {
            result[key] = definition.template[base];
        }
    }
    return result;
}
/** Tools switched per user by `tools.<tool>` and per organization by `moduleTools` (P-09). */
export const toolKeys = ['dialer', 'tickets', 'deals', 'financing', 'pkv'];
/** Tools every user has (Meine Tasks). */
export const alwaysAvailableTools = ['tasks'];
/** Module keys (Admin 05) and the tools they switch off for everyone regardless of permissions (P-09). */
export const moduleTools = {
    dialer: ['dialer'],
    financing: ['financing'],
    tickets_after_sales: ['tickets'],
    client_portal: [],
    video_consulting: [],
    property_acquisition: [],
    pkv: ['pkv'],
};
//# sourceMappingURL=permissions.js.map